// Tooltip: what an action key does, in the operator's words. It arrives after a short
// hover or on keyboard focus, travels a few pixels as it fades in, and leaves quickly.
// Never the only way to learn something: the control's own name still carries it.
// docs/operations/design-standard.md, "Controls".

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePresence } from './motion';

let seq = 0;
const DELAY_MS = 450;
// Tooltip groups: once one tooltip has shown, a neighbour reached within this window opens at
// once and without travel, so sweeping along a row of keys never waits at each one.
const GROUP_MS = 400;
let lastShownUntil = 0;
const EDGE = 8;

/** Keyboard focus only, so a mouse click does not pop a tooltip. An engine that cannot parse
 *  :focus-visible (older browsers, the test DOM) throws on it; treat that as keyboard focus. */
const focusVisible = (el: HTMLElement): boolean => {
  try { return el.matches(':focus-visible'); } catch { return true; }
};

export interface TooltipProps {
  /**
   * What happens, as a short phrase ("Start the timer"). Empty shows nothing and leaves the child
   * as it would be bare, for a hint that only applies sometimes. A line break starts a new line.
   */
  label: string;
  /** One quieter line under it: a reason it is disabled, or a hint. */
  note?: string;
  /** Prefer above (default) or below the target. */
  side?: 'top' | 'bottom';
  /** Only while the child's own text is cut off by its box: the full name behind an ellipsis. */
  truncated?: boolean;
  children: React.ReactElement<any>;
}

/** The child's text runs past its box (an ellipsis, a line clamp). */
const cutOff = (el: Element | null | undefined): boolean =>
  !!el && (el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1);

export const Tooltip: React.FC<TooltipProps> = ({ label, note, side = 'top', truncated, children }) => {
  const [id] = useState(() => `ui-tip-${++seq}`);
  const host = useRef<HTMLSpanElement>(null);
  const tip = useRef<HTMLSpanElement>(null);
  const timer = useRef<number | undefined>(undefined);
  const [show, setShow] = useState(false);
  const [instant, setInstant] = useState(false);
  const shown = useRef(false);
  shown.current = show;
  const [pos, setPos] = useState<{ left: number; top: number; below: boolean } | null>(null);
  const { mounted, open } = usePresence(show, 80);

  const place = useCallback(() => {
    const el = host.current?.firstElementChild || host.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const below = side === 'bottom' || r.top < 56;
    setPos({ left: r.left + r.width / 2, top: below ? r.bottom + 6 : r.top - 6, below });
  }, [side]);

  const enter = (delay: number) => {
    if (!label) return;
    if (truncated && !cutOff(host.current?.firstElementChild)) return;
    window.clearTimeout(timer.current);
    const grouped = Date.now() < lastShownUntil;
    setInstant(grouped);
    timer.current = window.setTimeout(() => { place(); setShow(true); }, grouped ? 0 : delay);
  };
  const hide = () => {
    window.clearTimeout(timer.current);
    if (shown.current) lastShownUntil = Date.now() + GROUP_MS;
    setShow(false);
  };
  // A short grace so the pointer can cross the gap onto the tooltip and stay there (WCAG 1.4.13).
  const leave = () => {
    window.clearTimeout(timer.current);
    if (!shown.current) return;
    timer.current = window.setTimeout(hide, 120);
  };
  const stay = () => window.clearTimeout(timer.current);

  useEffect(() => () => window.clearTimeout(timer.current), []);
  useEffect(() => { if (!label) hide(); }, [label]); // eslint-disable-line react-hooks/exhaustive-deps

  // Held inside the window: a key at the screen's edge would otherwise push half its tip off it.
  useLayoutEffect(() => {
    const el = tip.current;
    if (!mounted || !pos || !el || typeof window === 'undefined') return;
    const half = el.offsetWidth / 2;
    const left = Math.min(Math.max(pos.left, EDGE + half), Math.max(EDGE + half, window.innerWidth - EDGE - half));
    if (Math.abs(left - pos.left) > 0.5) setPos({ ...pos, left });
  }, [mounted, pos]);
  useEffect(() => {
    if (!show) return undefined;
    // Capture phase, and stopped: Escape dismisses the tooltip first, not the dialog around it.
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); hide(); } };
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('scroll', hide, true);
    return () => { window.removeEventListener('keydown', onKey, true); window.removeEventListener('scroll', hide, true); };
  }, [show]);

  // A disabled control takes neither the pointer nor focus, so its reason ("why can't I?") could
  // never show. Around a disabled child the wrapper becomes a focusable box that carries both.
  const blocked = !!label && !!children.props.disabled;
  const described = [children.props['aria-describedby'], show && !blocked ? id : undefined].filter(Boolean).join(' ') || undefined;
  const child = React.cloneElement(children, { 'aria-describedby': described });

  return (
    <span
      ref={host}
      // display: contents keeps the wrapper out of layout: the child sizes and aligns exactly as
      // it would bare. A box here made a control with negative margins come up short and truncate.
      className={blocked ? 'inline-flex max-w-full rounded-control focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50' : 'contents'}
      tabIndex={blocked ? 0 : undefined}
      aria-describedby={blocked && show ? id : undefined}
      onPointerEnter={(e) => { if (e.pointerType === 'mouse') enter(DELAY_MS); }}
      onPointerLeave={leave}
      onFocus={(e) => { if (focusVisible(e.target as HTMLElement)) enter(0); }}
      onBlur={hide}
      onPointerDown={hide}
    >
      {child}
      {mounted && pos && typeof document !== 'undefined' && createPortal(
        <span
          id={id}
          role="tooltip"
          style={{ left: pos.left, top: pos.top }}
          onPointerEnter={stay}
          onPointerLeave={leave}
          // Above every overlay in the app, legacy ones included (the highest sits at 10000).
          className={`fixed z-[10001] -translate-x-1/2 ${pos.below ? '' : '-translate-y-full'} ${open ? '' : 'pointer-events-none'}`}
        >
          {/* The outer box places; this one travels and fades, so the two transforms never fight. */}
          <span
            ref={tip}
            className={`block max-w-64 whitespace-pre-line rounded-control bg-surface-container-highest px-2 py-1 text-xs leading-4 text-on-surface shadow-overlay ${instant ? '' : 'transition-[opacity,transform]'} motion-reduce:translate-y-0 ${
              open
                ? 'translate-y-0 opacity-100 duration-standard ease-emphasized'
                : `opacity-0 duration-exit ease-exit ${pos.below ? '-translate-y-1' : 'translate-y-1'}`
            }`}
          >
            {label}
            {note && <span className="block text-on-surface-variant">{note}</span>}
          </span>
        </span>,
        document.body,
      )}
    </span>
  );
};

export default Tooltip;
